/**
 * How a lesson's call ends (decisions 53 to 59): nobody ends it mid-call.
 * The red button is Leave, for both roles, with no question; after it both
 * see "Done for today?" with Done and Rejoin, and what the other person is
 * doing, from their presence. Done is that person's confirmation that the
 * lesson happened. The lesson finishes when both have tapped Done, or when
 * the call closes 15 minutes after the scheduled end. At 10 minutes after
 * the start the clock decides a no-show. Pure, so tested.
 */
import type { PersonState } from './roomPresence.ts';
import { lessonsTarget } from './openInLessons.ts';

/** The call closes this long after the scheduled end (53). */
export const CALL_CLOSE_AFTER_END_MIN = 15;
/** A no-show is decided this long after the start (57, 58). */
export const NO_SHOW_AFTER_START_MIN = 10;

const MIN = 60_000;
type Timed = { scheduledStartAt: string; durationMinutes?: number | null };

export function callClosesAtMs(b: Timed): number {
  return new Date(b.scheduledStartAt).getTime() + ((b.durationMinutes ?? 45) + CALL_CLOSE_AFTER_END_MIN) * MIN;
}

/** The lesson is over: finished by the server, both confirmed Done, its call closed, or settled. */
export function lessonOver(b: Timed & {
  status: string; finishedAt?: string | null; roomClosedAt?: string | null;
  coachDone?: { at: string } | null; playerDone?: { at: string } | null;
}, nowMs: number): boolean {
  if (b.finishedAt || b.roomClosedAt || b.status === 'completed') return true;
  if (b.coachDone && b.playerDone) return true;
  return nowMs >= callClosesAtMs(b);
}

/**
 * Whether this side's call has ended, for the room. As lessonOver, except
 * after the player's rating (decisions 105 and 106): the coach's call ends
 * at once (coachMustLeave), and the player's stays open until the call
 * closes, for the row's "Call open until 1:40 AM · Rejoin".
 */
export function callEndedFor(b: Timed & {
  status: string; finishedAt?: string | null; finishedBy?: string | null; roomClosedAt?: string | null;
  coachDone?: { at: string } | null; playerDone?: { at: string } | null; coachMustLeave?: { at: string } | null;
}, isCoach: boolean, nowMs: number): boolean {
  if (isCoach && b.coachMustLeave) return true;
  if (!isCoach && b.finishedBy === 'player_rating' && !b.roomClosedAt && b.status === 'confirmed') {
    return nowMs >= callClosesAtMs(b);
  }
  return lessonOver(b, nowMs);
}

export type LessonKind = 'intro' | 'single' | 'month';
export function lessonKind(b: { offeringType: string; membershipId?: string | null }): LessonKind {
  if (b.offeringType === 'free_intro_call') return 'intro';
  return b.membershipId || b.offeringType === 'live_membership' ? 'month' : 'single';
}

/**
 * The line at 10 minutes after the start when the other person has not
 * joined. The coach's is decision 154's (the coach decides); the player's
 * is the server's own words for a coach miss (copy.ts, missSettled). Null
 * before then.
 */
export function noShowLine(p: { isCoach: boolean; otherFirst: string; kind: LessonKind; startIso: string; nowMs: number }): string | null {
  if (p.nowMs < new Date(p.startIso).getTime() + NO_SHOW_AFTER_START_MIN * MIN) return null;
  if (p.isCoach) {
    // Decision 154: nothing counts by the clock; the coach decides, on every lesson type. No "paid as usual" line.
    return `${p.otherFirst} didn't join. You decide.`;
  }
  const outcome = p.kind === 'single' ? "This lesson wasn't used, and you are refunded in full."
    : p.kind === 'intro' ? 'Nothing was charged.' : "This lesson wasn't used.";
  return `${p.otherFirst} didn't join. ${outcome}`;
}

/**
 * A player no-show in the call (154, 155; booking-contracts.md 13), while the other person is not in it:
 * the server's hold when there is one (playerNoShowStatus's line: the coach's "You decide." until decided,
 * then what was decided; the player's own lines), else the clock's line. The coach decides from here only
 * once the server holds it (pending): before then there is nothing yet to count or offer.
 */
export function noShowInCall(p: {
  isCoach: boolean; theyAreIn: boolean; clockLine: string | null; held: { line: string; action: string | null } | null;
}): { line: string; decide: boolean } | null {
  if (p.theyAreIn) return null;
  if (p.held) return { line: p.held.line, decide: p.isCoach && p.held.action === 'resolve' };
  return p.clockLine ? { line: p.clockLine, decide: false } : null;
}

export type DoneAction = 'done' | 'rejoin' | 'message' | 'lessons';

/**
 * "Done for today?", after this side left: the other person's state in one
 * line, and the ways on, the first the primary.
 */
export function doneForToday(p: {
  theirState: PersonState; otherFirst: string; isCoach: boolean; kind: LessonKind;
  startIso: string; nowMs: number; leftTime: string | null;
}): { title: string; line: string; actions: DoneAction[] } {
  const title = 'Done for today?';
  const first = p.otherFirst;
  switch (p.theirState) {
    case 'camera_on': case 'camera_off': case 'camera_paused':
      return { title, line: `${first} is still on the call`, actions: ['rejoin', 'done'] };
    case 'not_joined': {
      const noShow = noShowLine({ ...p, startIso: p.startIso });
      return noShow
        ? { title, line: noShow, actions: ['done'] }
        : { title, line: `${first} hasn't joined yet. We'll tell you the moment they do.`, actions: ['rejoin', 'message', 'lessons'] };
    }
    case 'dropped':
      return { title, line: `${first}'s connection dropped`, actions: ['done', 'rejoin'] };
    default:
      return { title, line: p.leftTime ? `${first} left the call at ${p.leftTime}` : `${first} left the call`, actions: ['done', 'rejoin'] };
  }
}

/** A join stamped this long before the screen opened still counts as news (clock skew). */
export const JOIN_NEWS_GRACE_MS = 10_000;

/**
 * "We'll tell you the moment they do": tell of their join only when they
 * are in now and their join is stamped after this screen opened. Presence
 * that reads "not joined" while it loads, then "in" for someone who was on
 * the call all along, is not news.
 */
export function joinIsNews(p: { theirState: PersonState; joinedAt: string | null | undefined; sinceMs: number }): boolean {
  const inNow = p.theirState === 'camera_on' || p.theirState === 'camera_off' || p.theirState === 'camera_paused';
  const at = p.joinedAt ? Date.parse(p.joinedAt) : NaN;
  return inNow && !Number.isNaN(at) && at >= p.sinceMs - JOIN_NEWS_GRACE_MS;
}

/**
 * How long the call ran, in minutes: the server's measure of both together,
 * else from both joins to the first leave (the one leave so far, while the
 * other is still on, so both Call ended screens give the same length), else
 * the booked length.
 */
export function callMinutes(b: Timed & {
  attendance?: { togetherMinutes?: number | null } | null;
  coachJoinedAt?: string | null; playerJoinedAt?: string | null; coachLeftAt?: string | null; playerLeftAt?: string | null;
}): number {
  if (b.attendance?.togetherMinutes) return b.attendance.togetherMinutes;
  const lefts = [b.coachLeftAt, b.playerLeftAt].filter((x): x is string => !!x).map(Date.parse);
  if (b.coachJoinedAt && b.playerJoinedAt && lefts.length) {
    const from = Math.max(Date.parse(b.coachJoinedAt), Date.parse(b.playerJoinedAt));
    const to = Math.min(...lefts);
    if (to > from) return Math.max(1, Math.round((to - from) / MIN));
  }
  return b.durationMinutes ?? 45;
}

/**
 * A player in after the 10-minute mark with the coach gone (decision 78):
 * the no-show stands, and the stage says "Ryan has left the call." Null
 * otherwise (the coach still on the call reverses it, and the lesson runs).
 */
export function lateJoinLine(p: {
  isCoach: boolean; ownJoinedAt: string | null | undefined; startIso: string; theirState: PersonState;
  /** When the coach left (their Leave, else their last sighting). */
  theirLeftAt: string | null | undefined; otherFirst: string;
}): string | null {
  if (p.isCoach || !p.ownJoinedAt || !p.theirLeftAt) return null;
  if (p.theirState !== 'left' && p.theirState !== 'dropped') return null;
  const joined = Date.parse(p.ownJoinedAt);
  const late = joined >= Date.parse(p.startIso) + NO_SHOW_AFTER_START_MIN * MIN;
  // The coach gone before the player came: the no-show stands. Still there then: reversed, the lesson ran.
  return late && Date.parse(p.theirLeftAt) <= joined ? `${p.otherFirst} has left the call.` : null;
}

/**
 * Where "Lessons" is for this person (decision 81: Done lands on Lessons):
 * a coach's Lessons tab is /orders, a player's /lessons, as the nav has it
 * (Layout, by currentMode). /lessons for a coach is the player's page, with
 * "Book a lesson to get started" and Browse Coaches.
 */
export function lessonsPath(currentMode: string | null | undefined): '/orders' | '/lessons' {
  return currentMode === 'coach' ? '/orders' : '/lessons';
}

/**
 * Where Done lands after the call (146, replacing 81): that lesson's Delivered page, which carries
 * Message, for both roles. On a wide screen that is Lessons on Delivered with the lesson in the pane
 * (135); on a phone, the lesson's own page. A guest has no lesson page: Lessons.
 */
export function afterCallTarget(p: { isCoach: boolean; guest: boolean; bookingId: string; narrow: boolean; currentMode: string | null | undefined }): { to: string; state?: { openLive: string } } {
  if (p.guest) return { to: lessonsPath(p.currentMode) };
  if (p.narrow) return { to: `/live/${p.bookingId}` };
  return lessonsTarget({ isCoach: p.isCoach, bookingId: p.bookingId, delivered: true });
}

/** The player's one Done after the call (105): their confirmation, with the stars and the review when given. */
export function playerDonePayload(bookingId: string, stars: number, review: string): {
  bookingId: string; rating?: { stars: number; text?: string };
} {
  if (!(stars >= 1 && stars <= 5)) return { bookingId };
  const text = review.trim();
  return { bookingId, rating: { stars, ...(text ? { text } : {}) } };
}

/** The review's title on Call ended (decisions 105, 134): what the player writes, the coach sees. */
export function reviewQuestion(coachFirst: string): string {
  return `Would you like to leave a quick review for ${coachFirst}? (optional)`;
}
